import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  FaUsers, 
  FaUserPlus, 
  FaDownload, 
  FaChevronLeft, 
  FaChevronRight, 
  FaTrash, 
  FaUserEdit,
  FaBuilding,
  FaProjectDiagram,
  FaSearch,
  FaFilter,
  FaTimes,
  FaSpinner
} from 'react-icons/fa';
import { collection, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './EmployeeReports.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../components/TableFilter';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useNavigate, Navigate } from 'react-router-dom';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import { useTheme } from '../contexts/ThemeContext'; // Import theme context
import useAllowedEmployeeIds from '../hooks/useAllowedEmployeeIds';

const EmployeeReports = () => {
  const { employeeId, employeeData, userAuthorization } = useUserProfile();
  const { isDarkMode } = useTheme(); // Use the theme context
  const navigate = useNavigate();

  // Check if user is admin
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');

  // SMO Lead / Bölge Yöneticisi görünürlük kısıtı — Operasyon Yönetimi ve SMO
  // Lead Raporları ile aynı kurallar (null = kısıtlama yok, Set = izinli id'ler)
  const allowedEmployeeIds = useAllowedEmployeeIds(isAdmin, employeeId);
  
  // State for centers and workers
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for projects
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  
  // State for insurances (retained from original)
  const [insurances, setInsurances] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredInsurances, setFilteredInsurances] = useState([]);
  
  // Filter and search state
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    center: true,
    project: true,
    role: true,
    rate: isAdmin,
    assignedAt: true
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Table scroll state
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExpiringTariffs, setShowExpiringTariffs] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  
  // Stats for display in cards
  const [stats, setStats] = useState({
    totalCenters: 0,
    totalProjects: 0,
    totalWorkers: 0
  });

  // Modified fetchCenters - now fetches all centers
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        setLoading(true);
        const centersRef = collection(db, 'Centers');
        const querySnapshot = await getDocs(centersRef);
        const centersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        const sortedCenters = centersData.sort((a, b) => a.Adı.localeCompare(b.Adı));
        setCenters(sortedCenters);
        setStats(prev => ({ ...prev, totalCenters: sortedCenters.length }));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching centers:', error);
        toast.error('Merkez listesi yüklenirken hata oluştu!');
        setLoading(false);
      }
    };

    fetchCenters();
  }, []);

  // Fixed project fetching and sorting
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsRef = collection(db, 'Projects');
        const projectsSnapshot = await getDocs(projectsRef);
        const projectsData = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Add null check and default to empty string for sorting
        const sortedProjects = projectsData.sort((a, b) => {
          const titleA = (a.titleShort || '').toString();
          const titleB = (b.titleShort || '').toString();
          return titleA.localeCompare(titleB);
        });
        
        setProjects(sortedProjects);
        setStats(prev => ({ ...prev, totalProjects: sortedProjects.length }));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Proje listesi yüklenirken hata oluştu!');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Proje listesi de görünürlük kısıtına tabidir: non-admin kullanıcılar yalnızca
  // kendi çalışanlarının görev aldığı projeleri seçebilir
  const visibleProjects = useMemo(() => {
    if (isAdmin) return projects;
    if (!allowedEmployeeIds) return [];
    return projects.filter((project) =>
      (project.employees || []).some((emp) => emp.id && allowedEmployeeIds.has(emp.id))
    );
  }, [projects, isAdmin, allowedEmployeeIds]);

  // Merkez listesi de aynı kısıta tabidir: non-admin kullanıcılar yalnızca
  // kendi çalışanlarının atandığı merkezleri seçebilir
  const visibleCenters = useMemo(() => {
    if (isAdmin) return centers;
    if (!allowedEmployeeIds) return [];
    const allowedCenterIds = new Set();
    projects.forEach((project) => {
      (project.employees || []).forEach((emp) => {
        if (emp.id && emp.centerId && allowedEmployeeIds.has(emp.id)) {
          allowedCenterIds.add(emp.centerId);
        }
      });
    });
    return centers.filter((center) => allowedCenterIds.has(center.id));
  }, [centers, projects, isAdmin, allowedEmployeeIds]);

  // Fetch workers when center or project is selected
  useEffect(() => {
    const fetchWorkers = async () => {
      if (!selectedProject && !selectedCenter) {
        setWorkers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const projectsRef = collection(db, 'Projects');
        let projectsQuery;

        if (selectedProject) {
          // If project is selected, fetch only that project
          projectsQuery = query(projectsRef, where('__name__', '==', selectedProject));
        } else {
          // If only center is selected, fetch all projects
          projectsQuery = projectsRef;
        }

        const projectsSnapshot = await getDocs(projectsQuery);
        let workersData = [];

        for (const projectDoc of projectsSnapshot.docs) {
          const projectData = projectDoc.data();
          const projectEmployees = projectData.employees || [];

          // Filter employees based on selected center if any
          const filteredEmployees = selectedCenter
            ? projectEmployees.filter(emp => emp.centerId === selectedCenter)
            : projectEmployees;

          filteredEmployees.forEach(emp => {
            const existingWorker = workersData.find(w => w.id === emp.id);
            // Find the center name from the centers array
            const centerInfo = centers.find(c => c.id === emp.centerId);
            const centerName = centerInfo ? centerInfo.Adı : '-';
            const projectInfo = {
              projectId: projectDoc.id,
              projectTitle: projectData.titleShort,
              role: emp.role,
              rate: emp.rate,
              assignedAt: emp.assignedAt,
              centerId: emp.centerId,
              centerName: centerName
            };

            if (existingWorker) {
              existingWorker.projects.push(projectInfo);
            } else {
              workersData.push({
                id: emp.id,
                name: emp.name,
                surname: emp.surname,
                projects: [projectInfo]
              });
            }
          });
        }
        
        // SMO Lead / Bölge Yöneticisi yalnızca kendi çalışanlarını görür;
        // izin listesi henüz çözülmediyse güvenli tarafta kal (kimseyi gösterme)
        if (!isAdmin) {
          workersData = allowedEmployeeIds
            ? workersData.filter((worker) => allowedEmployeeIds.has(worker.id))
            : [];
        }

        // Apply search filter if searchTerm exists
        if (searchTerm) {
          workersData = workersData.filter(worker =>
            `${worker.name} ${worker.surname}`.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        setWorkers(workersData);
        setStats(prev => ({ ...prev, totalWorkers: workersData.length }));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching workers:', error);
        toast.error('Çalışan bilgileri yüklenirken hata oluştu!');
        setLoading(false);
      }
    };

    fetchWorkers();
  }, [selectedProject, selectedCenter, searchTerm, centers, isAdmin, allowedEmployeeIds]);

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Handle click outside filters menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll detection
  useEffect(() => {
    const checkScroll = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        const hasScroll = scrollWidth > clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [workers]);

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [workers]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleEmployeeClick = async (worker, project) => {
    try {
      // Query visits collection for this specific employee and project
      const visitsRef = collection(db, 'Visits');
      const visitsQuery = query(
        visitsRef,
        where('employeeId', '==', worker.id),
        where('projectId', '==', project.projectId)
      );
      
      const visitsSnapshot = await getDocs(visitsQuery);
      
      if (visitsSnapshot.empty) {
        toast.info('Bu çalışanın visit\'i bulunamadı.');
        return;
      }

      setSelectedEmployee({
        id: worker.id,
        name: `${worker.name} ${worker.surname}`,
        project: {
          id: project.projectId,
          title: project.projectTitle
        },
        visits: visitsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      });
      setShowVisitsModal(true);
    } catch (error) {
      console.error('Error fetching visits:', error);
      toast.error('Visit bilgileri yüklenirken bir hata oluştu.');
    }
  };
  
  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };
  
  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setSelectedCenter('');
    setSelectedProject('');
  };

  const downloadExcel = () => {
    const dataToExport = workers.flatMap(worker => 
      worker.projects.map(project => ({
        'Çalışan Adı': `${worker.name} ${worker.surname}`,
        'Merkez': project.centerName || '-',
        'Proje': project.projectTitle,
        'Rol': project.role,
        'Tarife Tipi': isAdmin ? (project.rate?.type || '') : '',
        'Tarife Değeri': isAdmin ? (project.rate?.value || '') : '',
        'Atanma Tarihi': project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                        (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '')
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    
    // Get selected project and center names for the filename
    const selectedProjectName = projects.find(project => project.id === selectedProject)?.titleShort || 'Tüm_Projeler';
    const selectedCenterName = centers.find(center => center.id === selectedCenter)?.Adı || 'Tüm_Merkezler';
    
    // Create filename based on selections
    let filename = 'Çalışan_Listesi';
    if (selectedProject) filename = `${selectedProjectName}_${filename}`;
    if (selectedCenter) filename = `${filename}_${selectedCenterName}`;
    
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        {/* Header */}
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.title}>
                <FaUsers className={styles.headerIcon} />
                Çalışan Raporları
              </h1>
              <div className={styles.breadcrumb}>Raporlar / Çalışan Raporları</div>
            </div>
            <div className={styles.headerButtons}>
              <button 
                className={`${styles.button} ${styles.downloadButton}`} 
                onClick={downloadExcel}
                disabled={workers.length === 0}
              >
                <FaDownload className={styles.icon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        {/* Stats Cards */}
        <div className={styles.cardContainer}>
          <div className={`${styles.card} ${styles.cardTotal}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTitle}>Toplam Merkez Sayısı</div>
              <div className={styles.cardValue}>{isAdmin ? stats.totalCenters : visibleCenters.length}</div>
            </div>
            <FaBuilding className={styles.cardIcon} />
          </div>
          
          <div className={`${styles.card} ${styles.cardActive}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTitle}>Toplam Proje Sayısı</div>
              <div className={styles.cardValue}>{isAdmin ? stats.totalProjects : visibleProjects.length}</div>
            </div>
            <FaProjectDiagram className={styles.cardIcon} />
          </div>
          
          <div className={`${styles.card} ${styles.cardInactive}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTitle}>{selectedCenter || selectedProject ? 'Filtrelenmiş' : 'Toplam'} Çalışan Sayısı</div>
              <div className={styles.cardValue}>{stats.totalWorkers}</div>
            </div>
            <FaUsers className={styles.cardIcon} />
          </div>
        </div>

        {/* Selections Container */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className={styles.selectControl}
          >
            <option value="">Proje Seçiniz</option>
            {visibleProjects.map(project => (
              <option key={project.id} value={project.id}>
                {project.titleShort}
              </option>
            ))}
          </select>

          <select
            value={selectedCenter}
            onChange={(e) => setSelectedCenter(e.target.value)}
            className={styles.selectControl}
          >
            <option value="">Merkez Seçiniz</option>
            {visibleCenters.map(center => (
              <option key={center.id} value={center.id}>
                {center.Adı} - {center.İl}
              </option>
            ))}
          </select>
        </div>

        {/* Search & Filter Bar */}
        {(projects.length > 0 || centers.length > 0) && (
          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="İsim ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterCount}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterMenu}>
                  <div className={styles.filterHeader}>
                    <h3 className={styles.filterTitle}>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersButton}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4 className={styles.columnVisibilityTitle}>Sütun Görünürlüğü</h4>
                    <div className={styles.columnGrid}>
                      {Object.entries(visibleColumns).map(([column, isVisible]) => {
                        // Hide rate column checkbox for non-admin users
                        if (column === 'rate' && !isAdmin) return null;
                        
                        return (
                          <div key={column} className={styles.columnCheckbox}>
                            <input
                              type="checkbox"
                              id={`col-${column}`}
                              checked={isVisible}
                              onChange={() => toggleColumnVisibility(column)}
                            />
                            <label htmlFor={`col-${column}`} className={styles.columnLabel}>
                              {column === 'name' ? 'İsim' :
                               column === 'center' ? 'Merkez' :
                               column === 'project' ? 'Proje' :
                               column === 'role' ? 'Rol' :
                               column === 'rate' ? 'Tarife' :
                               'Atanma Tarihi'}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Table Section */}
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Veriler yükleniyor...</p>
          </div>
        ) : (
          <section className={styles.tableSection}>
            <div className={styles.tableWrapper}>
              <div className={styles.tableScrollContainer}>
                {showLeftScroll && (
                  <button
                    className={`${styles.scrollButton} ${styles.scrollLeft}`}
                    onClick={() => scrollTable('left')}
                  >
                    <FaChevronLeft />
                  </button>
                )}
                {showRightScroll && (
                  <button
                    className={`${styles.scrollButton} ${styles.scrollRight}`}
                    onClick={() => scrollTable('right')}
                  >
                    <FaChevronRight />
                  </button>
                )}
                <div
                  ref={tableContainerRef}
                  className={styles.tableContainer}
                  onScroll={handleScroll}
                >
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        {visibleColumns.name && <th>Çalışan</th>}
                        {visibleColumns.center && <th>Merkez</th>}
                        {visibleColumns.project && <th>Proje</th>}
                        {visibleColumns.role && <th>Rol</th>}
                        {visibleColumns.rate && <th>Tarife</th>}
                        {visibleColumns.assignedAt && <th>Atanma Tarihi</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {workers.length > 0 ? (
                        workers.flatMap((worker, workerIndex) => 
                          worker.projects.map((project, projectIndex) => (
                            <tr
                              key={`${worker.id}-${project.projectId}-${projectIndex}`}
                              onClick={() => handleEmployeeClick(worker, project)}
                              style={{ cursor: 'pointer' }}
                            >
                              {visibleColumns.name && <td>{worker.name} {worker.surname}</td>}
                              {visibleColumns.center && <td>{project.centerName || '-'}</td>}
                              {visibleColumns.project && <td>{project.projectTitle}</td>}
                              {visibleColumns.role && <td>{project.role}</td>}
                              {visibleColumns.rate && (
                                <td>
                                  {project.rate?.type ? `${project.rate.type} - ${project.rate.value || ''}` : '-'}
                                </td>
                              )}
                              {visibleColumns.assignedAt && (
                                <td>
                                  {project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                                   (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')}
                                </td>
                              )}
                            </tr>
                          ))
                        )
                      ) : (
                        <tr>
                          <td colSpan={Object.values(visibleColumns).filter(Boolean).length}>
                            <div className={styles.emptyState}>
                              <FaUsers className={styles.emptyStateIcon} />
                              <p className={styles.emptyStateText}>
                                {selectedCenter || selectedProject ? 'Seçilen kriterlere uygun çalışan bulunmamaktadır.' : 'Lütfen bir merkez veya proje seçiniz.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ProjectVisitsModal */}
        {selectedEmployee && (
          <ProjectVisitsModal
            show={showVisitsModal}
            onHide={() => {
              setShowVisitsModal(false);
              setSelectedEmployee(null);
            }}
            project={selectedEmployee.project}
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            visits={selectedEmployee.visits}
          />
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />
      </div>
    </div>
  );
};

export default EmployeeReports;